import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const galleryItems = [
  {
    id: 1,
    title: 'Ceramic Finish on Family SUV',
    category: 'Ceramic Coating',
    image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1080&q=80',
    description: 'Foam bath and paint preparation on a mid-size family SUV before ceramic coating.'
  },
  {
    id: 2,
    title: 'Tesla Interior Sanitization',
    category: 'Interior Detail',
    image: 'https://images.unsplash.com/photo-1527689368864-3a821dbccc34?auto=format&fit=crop&w=1080&q=80',
    description: 'Deep clean and conditioning of high-touch interior surfaces for a daily driver EV.'
  },
  {
    id: 3,
    title: 'Commercial Van Paint Correction',
    category: 'Paint Correction',
    image: 'https://images.unsplash.com/photo-1541844053589-346841d0c6a8?auto=format&fit=crop&w=1080&q=80',
    description: 'Two-stage correction and rinse on a service van to remove oxidation and road grime.'
  },
];

const categories = ['All', 'Ceramic Coating', 'Paint Correction', 'Interior Detail'];

export default function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredItems = galleryItems.filter(
    item => selectedCategory === 'All' || item.category === selectedCategory
  );

  return (
    <section className="relative overflow-hidden py-20">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#20070b] via-[#160508] to-[#20070b]" />
      <div className="mx-auto max-w-6xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">Recent projects</h2>
          <p className="text-sm text-white/60 sm:text-base">
            Every mobile visit includes full protection—here are the transformations our clients rave
            about.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div 
          className="mb-12 flex flex-wrap justify-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {categories.map((category) => (
            <motion.button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition ${
                selectedCategory === category
                  ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/30'
                  : 'border border-white/10 bg-white/5 text-white/70 hover:border-white/30 hover:text-white'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        {/* Gallery Grid */}
        <motion.div
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          layout
        >
          {filteredItems.map((item) => (
            <motion.div
              key={item.id}
              layoutId={`container-${item.id}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              className="group cursor-pointer rounded-3xl border border-white/10 bg-[#1c0d11]/75 p-3 transition hover:border-white/30"
              onClick={() => setSelectedImage(item)}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                <motion.img
                  src={item.image}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1c0d11]/90 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                    <p className="text-sm text-white/70">{item.category}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            >
              <motion.div
                layoutId={`container-${selectedImage.id}`}
                className="relative w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-black/80 backdrop-blur"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.image}
                  alt={selectedImage.title}
                  className="h-auto w-full"
                />
                <div className="space-y-2 p-6 text-sm text-white/70">
                  <h3 className="text-xl font-semibold text-white">{selectedImage.title}</h3>
                  <p>{selectedImage.description}</p>
                </div>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute right-4 top-4 rounded-full border border-white/20 bg-black/40 p-2 transition hover:border-white/40 hover:bg-black/60"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
